import { readonly, ref } from 'vue';

import { lateSubmissionPoliciesAvailable } from '@/policies/LateSubmissionPolicyPolicy.js';
import service from '@/services/activityDueDateRecalculationService.js';

// Shared by the autosaver, which saves the due date, and the dialog that asks about it.
const prompt = ref(null);
const result = ref(null);
const error = ref('');
const busy = ref(false);
const lastChecked = new Map();

function reset() {
    prompt.value = null;
    result.value = null;
    error.value = '';
    busy.value = false;
    lastChecked.clear();
}

/**
 * After a due-date save, asks the server whether graded late submissions were priced
 * against an earlier date. Stays silent when the policy feature is off, the date is
 * cleared, the same date was already checked, or nothing would change.
 */
async function checkAfterSave(activity) {
    const settings = activity?.activity_settings;
    const date = settings?.due_at ? new Date(settings.due_at) : null;

    if (!lateSubmissionPoliciesAvailable() || !activity?.id || !date || Number.isNaN(date.getTime())) return;

    const dueAt = date.toISOString();
    if (lastChecked.get(activity.id) === dueAt) return;
    lastChecked.set(activity.id, dueAt);

    try {
        const preview = await service.preview(activity.id, dueAt);
        if (preview.eligible > 0) {
            result.value = null;
            error.value = '';
            prompt.value = { activityId: activity.id, dueAt, eligible: preview.eligible };
        }
    } catch {
        // The date is saved either way. A failed lookup only means no prompt.
    }
}

async function confirm() {
    if (!prompt.value || busy.value) return;

    busy.value = true;
    error.value = '';
    try {
        result.value = await service.run(prompt.value.activityId, prompt.value.dueAt);
    } catch (exception) {
        error.value = exception?.response?.data?.message || 'The grades could not be recalculated. Try again.';
    } finally {
        busy.value = false;
    }
}

function dismiss() {
    if (busy.value) return;

    prompt.value = null;
    result.value = null;
    error.value = '';
}

export default function useDueDateExtensionPrompt() {
    return {
        prompt: readonly(prompt),
        result: readonly(result),
        error: readonly(error),
        busy: readonly(busy),
        checkAfterSave,
        confirm,
        dismiss,
        reset,
    };
}
